import React, {
    useEffect,
    useState
} from "react";

import {
    Link,
    useNavigate,
    useParams
} from "react-router-dom";

import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function EditUser() {

    /*
    |--------------------------------------------------------------------------
    | User ID
    |--------------------------------------------------------------------------
    */

    const { userId } = useParams();

    const navigate = useNavigate();


    /*
    |--------------------------------------------------------------------------
    | API URL
    |--------------------------------------------------------------------------
    */

    const API_URL =
        `${API_BASE_URL}/admin/users/${encodeURIComponent(
            userId
        )}`;


    /*
    |--------------------------------------------------------------------------
    | User Form
    |--------------------------------------------------------------------------
    */

    const [formData, setFormData] = useState({

        user_id: "",

        fullname: "",

        email: "",

        password: "",

        mobile: "",

        address: "",

        city: "",

        batch_year: "",

        college: ""

    });


    /*
    |--------------------------------------------------------------------------
    | Papers
    |--------------------------------------------------------------------------
    */

    const [papers, setPapers] = useState([]);


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    const [loading, setLoading] =
        useState(true);


    /*
    |--------------------------------------------------------------------------
    | Saving
    |--------------------------------------------------------------------------
    */

    const [saving, setSaving] =
        useState(false);


    /*
    |--------------------------------------------------------------------------
    | Error
    |--------------------------------------------------------------------------
    */

    const [error, setError] =
        useState("");


    /*
    |--------------------------------------------------------------------------
    | Fetch User
    |--------------------------------------------------------------------------
    */

    const fetchUser = async () => {

        try {

            setLoading(true);

            setError("");


            const response = await fetch(
                API_URL,
                {
                    method: "GET",

                    headers: {
                        Accept:
                            "application/json"
                    },

                    credentials: "include"
                }
            );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 401
            ) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );
            }


            /*
            |--------------------------------------------------------------------------
            | Other Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    `Server Error: ${response.status}`
                );
            }


            /*
            |--------------------------------------------------------------------------
            | JSON
            |--------------------------------------------------------------------------
            */

            const data =
                await response.json();


            console.log(
                "Edit User Response:",
                data
            );


            if (!data.success) {

                throw new Error(
                    data.message ||
                    "Unable to fetch user."
                );
            }


            /*
            |--------------------------------------------------------------------------
            | User Data
            |--------------------------------------------------------------------------
            */

            const user =
                data.user;


            setFormData({

                user_id:
                    user.user_id || "",

                fullname:
                    user.fullname || "",

                email:
                    user.email || "",

                password:
                    "",

                mobile:
                    user.mobile || "",

                address:
                    user.address || "",

                city:
                    user.city || "",

                batch_year:
                    user.batch_year || "",

                college:
                    user.college || ""

            });


            /*
            |--------------------------------------------------------------------------
            | Papers
            |--------------------------------------------------------------------------
            */

            setPapers(
                Array.isArray(
                    data.papers
                )
                    ? data.papers
                    : []
            );


        } catch (error) {

            console.error(
                "Edit User Fetch Error:",
                error
            );


            setError(
                error.message ||
                "Unable to fetch user."
            );


        } finally {

            setLoading(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Load User
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchUser();

    }, [userId]);


    /*
    |--------------------------------------------------------------------------
    | Input Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (event) => {

        const {
            name,
            value
        } = event.target;


        setFormData(
            (previous) => ({

                ...previous,

                [name]: value

            })
        );
    };


    /*
    |--------------------------------------------------------------------------
    | Paper Checkbox
    |--------------------------------------------------------------------------
    */

    const handlePaperChange = (
        paperId
    ) => {

        setPapers(
            (previous) => {

                return previous.map(
                    (paper) => {

                        if (
                            paper.id === paperId
                        ) {

                            return {

                                ...paper,

                                purchased:
                                    !paper.purchased

                            };
                        }


                        return paper;
                    }
                );
            }
        );
    };


    /*
    |--------------------------------------------------------------------------
    | Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (
        event
    ) => {

        event.preventDefault();


        try {

            setSaving(true);

            setError("");


            /*
            |--------------------------------------------------------------------------
            | Selected Papers
            |--------------------------------------------------------------------------
            */

            const selectedPapers =
                papers
                    .filter(
                        (paper) =>
                            paper.purchased === true
                    )
                    .map(
                        (paper) =>
                            paper.paper
                    );


            /*
            |--------------------------------------------------------------------------
            | Request Body
            |--------------------------------------------------------------------------
            */

            const requestData = {

                fullname:
                    formData.fullname,

                email:
                    formData.email,

                password:
                    formData.password,

                mobile:
                    formData.mobile,

                address:
                    formData.address,

                city:
                    formData.city,

                batch_year:
                    formData.batch_year,

                college:
                    formData.college,

                papers:
                    selectedPapers

            };


            console.log(
                "Update User Request:",
                requestData
            );


            /*
            |--------------------------------------------------------------------------
            | PUT API
            |--------------------------------------------------------------------------
            */

            const response =
                await fetch(
                    API_URL,
                    {

                        method: "PUT",

                        headers: {

                            "Content-Type":
                                "application/json",

                            Accept:
                                "application/json"

                        },

                        credentials:
                            "include",

                        body:
                            JSON.stringify(
                                requestData
                            )

                    }
                );


            /*
            |--------------------------------------------------------------------------
            | JSON
            |--------------------------------------------------------------------------
            */

            const data =
                await response.json();


            console.log(
                "Update User Response:",
                data
            );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 401
            ) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );
            }


            /*
            |--------------------------------------------------------------------------
            | Validation
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 422
            ) {

                const validationErrors =
                    data.errors || {};


                const firstError =
                    Object.values(
                        validationErrors
                    )[0];


                throw new Error(
                    Array.isArray(firstError)
                        ? firstError[0]
                        : "Please check the form."
                );
            }


            /*
            |--------------------------------------------------------------------------
            | Server Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    data.error ||
                    data.message ||
                    `Server Error: ${response.status}`
                );
            }


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (data.success) {

                await Swal.fire({

                    icon: "success",

                    title:
                        "User Updated",

                    text:
                        "User information and papers updated successfully.",

                    confirmButtonText:
                        "OK"

                });

                window.location.reload();

            } else {

                throw new Error(
                    data.message ||
                    "Unable to update user."
                );
            }


        } catch (error) {

            console.error(
                "Update User Error:",
                error
            );


            setError(
                error.message ||
                "Unable to update user."
            );


            Swal.fire({

                icon: "error",

                title: "Update Failed",

                text:
                    error.message ||
                    "Unable to update user."

            });


        } finally {

            setSaving(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div
                className="
                    container-fluid
                    d-flex
                    justify-content-center
                    align-items-center
                "
                style={{
                    minHeight: "400px"
                }}
            >

                <div className="text-center">

                    <div
                        className="
                            spinner-border
                            text-primary
                        "
                    >
                    </div>


                    <div
                        className="
                            mt-3
                            text-muted
                        "
                    >
                        Loading user...
                    </div>

                </div>

            </div>

        );
    }


    /*
    |--------------------------------------------------------------------------
    | Main
    |--------------------------------------------------------------------------
    */

    return (

        <div
    className="container-fluid"
    style={{
        maxHeight: "80vh",
        overflowY: "auto"
    }}
>


            {/* =====================================================
                Header
            ===================================================== */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <div>

                    <h3 className="mb-1">
                        Edit User
                    </h3>

                    <p className="text-muted mb-0">
                        Update user information and papers
                    </p>

                </div>


                <Link
                    to="/users"
                    className="
                        btn
                        btn-outline-secondary
                    "
                >

                    <i
                        className="
                            fa
                            fa-arrow-left
                            me-2
                        "
                    ></i>

                    Back

                </Link>

            </div>


            {/* =====================================================
                Error
            ===================================================== */}

            {error && (

                <div
                    className="
                        alert
                        alert-danger
                    "
                >
                    {error}
                </div>

            )}


            <form
                onSubmit={
                    handleSubmit
                }
            >


                {/* =================================================
                    SECTION 1
                ================================================= */}

                <div
                    className="
                        card
                        shadow-sm
                        mb-4
                    "
                >

                    <div
                        className="
                            card-header
                            bg-white
                        "
                    >

                        <h5 className="mb-0">
                            User Information
                        </h5>

                    </div>


                    <div className="card-body">

                        <div className="row g-3">


                            {/* User ID */}

                            <div className="col-md-6">

                                <label
                                    className="
                                        form-label
                                        fw-semibold
                                    "
                                >
                                    User ID
                                </label>


                                <input
                                    type="text"
                                    className="
                                        form-control
                                        bg-light
                                    "
                                    value={
                                        formData.user_id
                                    }
                                    readOnly
                                />


                                <small
                                    className="
                                        text-muted
                                    "
                                >
                                    User ID cannot be changed.
                                </small>

                            </div>


                            {/* Full Name */}

                            <div className="col-md-6">

                                <label
                                    className="
                                        form-label
                                        fw-semibold
                                    "
                                >

                                    Full Name

                                    <span className="text-danger">
                                        *
                                    </span>

                                </label>


                                <input
                                    type="text"
                                    name="fullname"
                                    className="form-control"
                                    value={
                                        formData.fullname
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    maxLength="30"
                                    required
                                />

                            </div>


                            {/* Email */}

                            <div className="col-md-6">

                                <label
                                    className="
                                        form-label
                                        fw-semibold
                                    "
                                >

                                    Email

                                    <span className="text-danger">
                                        *
                                    </span>

                                </label>


                                <input
                                    type="email"
                                    name="email"
                                    className="form-control"
                                    value={
                                        formData.email
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    maxLength="40"
                                    required
                                />

                            </div>


                            {/* Password */}

                            <div className="col-md-6">

                                <label
                                    className="
                                        form-label
                                        fw-semibold
                                    "
                                >
                                    Password
                                </label>


                                <input
                                    type="password"
                                    name="password"
                                    className="form-control"
                                    value={
                                        formData.password
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder="
                                        Leave blank to keep old password
                                    "
                                />


                                <small
                                    className="
                                        text-muted
                                    "
                                >
                                    Leave blank if you do not want to change
                                    the password.
                                </small>

                            </div>


                            {/* Mobile */}

                            <div className="col-md-6">

                                <label
                                    className="
                                        form-label
                                        fw-semibold
                                    "
                                >

                                    Mobile

                                    <span className="text-danger">
                                        *
                                    </span>

                                </label>


                                <input
                                    type="text"
                                    name="mobile"
                                    className="form-control"
                                    value={
                                        formData.mobile
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    maxLength="15"
                                    required
                                />

                            </div>


                            {/* City */}

                            <div className="col-md-6">

                                <label
                                    className="
                                        form-label
                                        fw-semibold
                                    "
                                >

                                    City

                                    <span className="text-danger">
                                        *
                                    </span>

                                </label>


                                <input
                                    type="text"
                                    name="city"
                                    className="form-control"
                                    value={
                                        formData.city
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    maxLength="50"
                                    required
                                />

                            </div>


                            {/* Batch Year */}

                            <div className="col-md-6">

                                <label
                                    className="
                                        form-label
                                        fw-semibold
                                    "
                                >

                                    Batch Year

                                    <span className="text-danger">
                                        *
                                    </span>

                                </label>


                                <input
                                    type="text"
                                    name="batch_year"
                                    className="form-control"
                                    value={
                                        formData.batch_year
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    maxLength="10"
                                    required
                                />

                            </div>


                            {/* College */}

                            <div className="col-md-6">

                                <label
                                    className="
                                        form-label
                                        fw-semibold
                                    "
                                >

                                    College

                                    <span className="text-danger">
                                        *
                                    </span>

                                </label>


                                <input
                                    type="text"
                                    name="college"
                                    className="form-control"
                                    value={
                                        formData.college
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    maxLength="100"
                                    required
                                />

                            </div>


                            {/* Address */}

                            <div className="col-12">

                                <label
                                    className="
                                        form-label
                                        fw-semibold
                                    "
                                >

                                    Address

                                    <span className="text-danger">
                                        *
                                    </span>

                                </label>


                                <textarea
                                    name="address"
                                    className="form-control"
                                    rows="3"
                                    value={
                                        formData.address
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    required
                                />

                            </div>

                        </div>

                    </div>

                </div>


                {/* =================================================
                    SECTION 2
                ================================================= */}

                <div
                    className="
                        card
                        shadow-sm
                        mb-4
                    "
                >

                    <div
                        className="
                            card-header
                            bg-white
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >

                        <div>

                            <h5 className="mb-1">
                                User Papers
                            </h5>

                            <small
                                className="text-muted"
                            >
                                Select the papers purchased by this user.
                            </small>

                        </div>


                        <span
                            className="
                                badge
                                bg-primary
                            "
                        >
                            {
                                papers.filter(
                                    (paper) =>
                                        paper.purchased
                                ).length
                            }{" "}
                            Selected
                        </span>

                    </div>


                    <div className="card-body">


                        {papers.length === 0 ? (

                            <div
                                className="
                                    text-center
                                    text-muted
                                    py-4
                                "
                            >

                                No enabled papers found.

                            </div>

                        ) : (

                            <div className="row g-3">

                                {papers.map(
                                    (paper) => (

                                        <div
                                            className="
                                                col-md-6
                                                col-lg-4
                                            "
                                            key={
                                                paper.id
                                            }
                                        >

                                            <div
                                                className={`
                                                    border
                                                    rounded
                                                    p-3
                                                    h-100
                                                    ${
                                                        paper.purchased
                                                            ? "border-success bg-light"
                                                            : ""
                                                    }
                                                `}
                                            >

                                                <div
                                                    className="
                                                        form-check
                                                    "
                                                >

                                                    <input
                                                        type="checkbox"
                                                        className="
                                                            form-check-input
                                                        "
                                                        id={
                                                            `paper-${paper.id}`
                                                        }
                                                        checked={
                                                            Boolean(
                                                                paper.purchased
                                                            )
                                                        }
                                                        onChange={() =>
                                                            handlePaperChange(
                                                                paper.id
                                                            )
                                                        }
                                                    />


                                                    <label
                                                        className="
                                                            form-check-label
                                                            fw-semibold
                                                        "
                                                        htmlFor={
                                                            `paper-${paper.id}`
                                                        }
                                                    >

                                                        {
                                                            paper.paper
                                                        }

                                                    </label>

                                                </div>


                                                <div
                                                    className="
                                                        mt-2
                                                    "
                                                >

                                                    {paper.purchased ? (

                                                        <span
                                                            className="
                                                                badge
                                                                bg-success
                                                            "
                                                        >
                                                            Purchased
                                                        </span>

                                                    ) : (

                                                        <span
                                                            className="
                                                                badge
                                                                bg-secondary
                                                            "
                                                        >
                                                            Not Purchased
                                                        </span>

                                                    )}

                                                </div>

                                            </div>

                                        </div>

                                    )
                                )}

                            </div>

                        )}

                    </div>

                </div>


                {/* =================================================
                    Save Buttons
                ================================================= */}

                <div
                    className="
                        d-flex
                        justify-content-end
                        gap-2
                        mb-5
                    "
                >

                    <Link
                        to="/users"
                        className="
                            btn
                            btn-outline-secondary
                        "
                    >
                        Cancel
                    </Link>


                    <button
                        type="submit"
                        className="
                            btn
                            btn-primary
                        "
                        disabled={saving}
                    >

                        {saving ? (

                            <>
                                <span
                                    className="
                                        spinner-border
                                        spinner-border-sm
                                        me-2
                                    "
                                ></span>

                                Saving...

                            </>

                        ) : (

                            <>
                                <i
                                    className="
                                        fa
                                        fa-save
                                        me-2
                                    "
                                ></i>

                                Save Changes

                            </>
                        )}

                    </button>

                </div>

            </form>

        </div>

    );
}


export default EditUser;